// src/components/Navbar.js
import { signOut } from '@aws-amplify/auth';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';

// ✅ Use a robot-only image (transparent PNG/SVG). Update the path/name:
import robotIcon from '../assets/robot-icon.png';

import { useEffect, useRef, useState } from 'react';

export default function Navbar({ userEmail }) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const userIsAdmin = false; // Placeholder until you connect to auth

  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeMobile = () => setMobileOpen(false);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setDropdownOpen(false), 100);
  };

  return (
    <div className="navbar-wrapper">
      <div className="navbar-inner">
        {/* Left: Brand (icon + name) */}
        <button
          className="navbar-brandBtn"
          onClick={() => navigate('/')}
          aria-label="Go to homepage"
        >
          <div className="navbar-brand">
            <img
              src={robotIcon}
              alt="ChronoTech Robotics robot logo"
              className="navbar-logo"
              width="64"
              height="64"
              loading="eager"
              decoding="async"
            />
            <span className="navbar-name">ChronoTech Robotics</span>
          </div>
        </button>

        {/* Center: Desktop nav */}
        <nav className="navbar-center" aria-label="Primary">
          <Link to="/" className="navbar-link large">Home</Link>
          <Link to="/robokidslanding" className="navbar-link large">Robo Kids Learning</Link>
          <Link to="/innovator" className="navbar-link large">Innovator Program</Link>
          <Link to="/news-guides" className="navbar-link large">News &amp; Guides</Link>
          <Link to="/shop" className="navbar-link large">Shop</Link>
          <Link to="/robots" className="navbar-link large">Robot Dashboard</Link>

          {/* More dropdown */}
          <div
            ref={dropdownRef}
            className="navbar-dropdown"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              className="navbar-link dropdown-toggle"
              role="button"
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              onClick={() => setDropdownOpen(v => !v)}
            >
              More ▾
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu" role="menu">
                <Link to="/membership" className="dropdown-item" role="menuitem" onClick={() => setDropdownOpen(false)}>
                  Membership
                </Link>
                <Link to="/about" className="dropdown-item" role="menuitem" onClick={() => setDropdownOpen(false)}>
                  About
                </Link>
                <Link to="/contact" className="dropdown-item" role="menuitem" onClick={() => setDropdownOpen(false)}>
                  Contact
                </Link>
                <Link to="/follow" className="dropdown-item" role="menuitem" onClick={() => setDropdownOpen(false)}>
                  Follow Us
                </Link>
              </div>
            )}
          </div>

          {userEmail && <Link to="/profile" className="navbar-link large">Profile</Link>}
          {userIsAdmin && <Link to="/admin" className="navbar-link large">Admin Page</Link>}
        </nav>

        {/* Right: Logout + Burger */}
        <div className="navbar-right">
          {userEmail && (
            <button onClick={handleLogout} className="navbar-button">Logout</button>
          )}
          <button
            className="navbar-burger"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(v => !v)}
          >
            ☰
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      {mobileOpen && (
        <div className="mobile-sheet" role="dialog" aria-modal="true" aria-label="Mobile navigation">
          <Link to="/" onClick={closeMobile} className="mItem">Home</Link>
          <Link to="/robokidslanding" onClick={closeMobile} className="mItem">Robo Kids Learning</Link>
          <Link to="/innovator" onClick={closeMobile} className="mItem">Innovator Program</Link>
          <Link to="/news-guides" onClick={closeMobile} className="mItem">News &amp; Guides</Link>
          <Link to="/shop" onClick={closeMobile} className="mItem">Shop</Link>
          <Link to="/membership" onClick={closeMobile} className="mItem">Membership</Link>
          <Link to="/about" onClick={closeMobile} className="mItem">About</Link>
          <Link to="/contact" onClick={closeMobile} className="mItem">Contact</Link>
          <Link to="/follow" onClick={closeMobile} className="mItem">Follow Us</Link>
          <Link to="/robots" onClick={closeMobile} className="mItem">Robotics</Link>

          {userEmail && (
            <div className="mActions">
              <Link to="/profile" onClick={closeMobile} className="mItem mGhost">Profile</Link>
              <button className="mItem mBtn" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
      )}

      <p className="navbar-tagline">
        Empowering Kids to Build the Future with Robotic Engineering
      </p>
    </div>
  );
}
